/**
 * Coverage + override-conflict copy (spec 07-COV §5). Maps the server seams to
 * plain-words sentences and composes the banner reason list with the SAME
 * joinClauses rule 06-VER uses (one composed notice, never a second stacked
 * banner). Property vocabulary throughout. Visual design is UX-owed; this is the
 * functional copy the readout, conflict list, and banner consume.
 */

import { countText } from 'utils/number';
import { capitalizeFirstLetter, joinClauses, pluralize } from 'utils/strings';
import type {
  CoverageCounts,
  OverrideConflict,
  OverrideConflictKind,
} from '../schedule-dialog/types';

/** Conflict enum → the plain-words phrase the list + banner render it as (§5). */
export const CONFLICT_SENTENCES: Record<OverrideConflictKind, string> = {
  beforeReported: 'earlier than a reported date',
  outOfLifecycleOrder: 'out of order after the re-run',
  slotNotProduced: 'this scenario does not schedule this property',
};

/** Fallback for an unrecognized (future) server conflict enum — never a raw key. */
export const GENERIC_CONFLICT_SENTENCE = 'no longer fits the schedule';

/**
 * The plain-words phrase for one conflict kind; an unknown value degrades to the
 * generic sentence rather than leaking a raw enum.
 *
 * @param {OverrideConflictKind} kind - A server conflict enum (possibly future).
 * @return {string} The human phrase.
 */
export function conflictSentence(kind: OverrideConflictKind): string {
  return CONFLICT_SENTENCES[kind] ?? GENERIC_CONFLICT_SENTENCE;
}

/**
 * The banner reason clauses (§5) — unscheduled, unvalued, and conflict counts as
 * sentences, in that order. Empty when nothing applies, so gating on non-empty is
 * the whole suppression rule (empty renders no banner).
 *
 * @param {CoverageCounts | undefined} coverage - The read's coverage counts, if any.
 * @param {OverrideConflict[]} conflicts - The read's override conflicts.
 * @return {string[]} The cause clauses (empty when none).
 */
export function coverageBannerClauses(
  coverage: CoverageCounts | undefined,
  conflicts: OverrideConflict[]
): string[] {
  const clauses: string[] = [];
  const unscheduled = coverage?.unscheduledCount ?? 0;
  const unvalued = coverage?.unvaluedCount ?? 0;
  const conflictCount = conflicts.length;

  if (unscheduled > 0) {
    clauses.push(
      `${countText(unscheduled)} ${pluralize(
        unscheduled,
        'property is',
        'properties are'
      )} unscheduled`
    );
  }
  if (unvalued > 0) {
    clauses.push(
      `${countText(unvalued)} ${pluralize(
        unvalued,
        'property',
        'properties'
      )} will not be valued`
    );
  }
  if (conflictCount > 0) {
    clauses.push(
      `${countText(conflictCount)} ${pluralize(
        conflictCount,
        'override',
        'overrides'
      )} ` +
        `${pluralize(
          conflictCount,
          'no longer fits',
          'no longer fit'
        )} the schedule`
    );
  }
  return clauses;
}

/**
 * The banner clauses that are NOT already stated as their own readout line
 * (INS-12496 defect B) — override conflicts only. The unscheduled and unvalued
 * causes each render a base-named readout line with a "View" drill-in, so
 * repeating them in the banner is the verbatim "second stacked banner" copy.ts
 * forbids (the reporter saw "5 properties will not be valued" twice). Conflicts
 * have no readout line — only the table below — so a one-line composed summary of
 * them is the banner's remaining job. Empty when there are no conflicts.
 *
 * Implemented over {@link coverageBannerClauses} with `coverage` omitted, so the
 * unscheduled/unvalued branches are skipped and only the conflict clause remains
 * — one composer, one plural/format rule.
 *
 * @param {OverrideConflict[]} conflicts - The read's override conflicts.
 * @return {string[]} The residual (conflict-only) banner clauses.
 */
export function residualBannerClauses(conflicts: OverrideConflict[]): string[] {
  return coverageBannerClauses(undefined, conflicts);
}

/**
 * The single composed banner subtitle for the clause list — sentence-cased and
 * joined with the shared joinClauses rule. Empty ⇒ '' (caller gates on non-empty).
 *
 * @param {string[]} clauses - The composed cause clauses.
 * @return {string} The banner subtitle.
 */
export function coverageBannerSubtitle(clauses: string[]): string {
  if (clauses.length === 0) {
    return '';
  }
  return capitalizeFirstLetter(joinClauses(clauses));
}
